// 按下推進鈕被擋下之後，把第一個出錯題目的標題與錯誤說明捲到看得見的地方。
//
// 為什麼不直接對題目區塊 scrollIntoView：題目比捲動區還高時（選項很多的題），block: 'nearest'
// 只捲最少的距離，標題反而被推出上緣，讀者看到的是一堆選項而不是哪一題出錯；block: 'start' 則會
// 把整個 widget 拉到宿主視窗頂端，嵌在新聞文章裡的 widget 不該拉動宿主頁面。所以分兩步：
//   1. 只動 widget 自己的捲動容器（往上找，止於 shadow root），讓標題落在容器上緣附近；
//   2. 容器內到位之後，才對標題與錯誤說明各做一次 nearest，由瀏覽器決定宿主視窗要不要最小幅度跟進。

/** 標題離捲動容器上緣的留白；貼著裁切邊的話，上一題的尾巴會被切成半截。 */
export const REVEAL_TOP_INSET_PX = 12

export interface VerticalRange {
  top: number
  bottom: number
}

/**
 * 容器要捲到哪個 scrollTop，標題到錯誤說明這一段才看得見。三個參數用同一個座標系
 * （viewport，即 getBoundingClientRect 的 top／bottom）；view 是容器中讀者實際看得到的範圍，
 * span 是標題頂到錯誤說明底。
 *
 * 整段已在 view 內就不動；否則一律把標題對齊到上緣（留白 REVEAL_TOP_INSET_PX）。不做「只把
 * 錯誤拉進來」的最小位移：標題下的選項要跟著進視窗，錯誤也才不會停在下緣邊上。整段比 view 還高
 * 時同樣以標題為準。
 */
export function revealScrollTop(scrollTop: number, view: VerticalRange, span: VerticalRange): number {
  if (span.top >= view.top && span.bottom <= view.bottom)
    return scrollTop
  return Math.max(0, scrollTop + (span.top - view.top - REVEAL_TOP_INSET_PX))
}

// 真正會捲動的最近祖先：內容比自己高，且 overflow-y 是 auto／scroll。overflow:hidden 的祖先即使
// 內容比它高也不算 —— 讀者捲不動它，對它寫 scrollTop 卻會把版面程式化地推走。往上走到 shadow root
// 為止（頂層節點的 parentElement 是 null），不會碰到宿主頁面的捲動容器。
function scrollContainerOf(el: Element): HTMLElement | null {
  for (let node = el.parentElement; node !== null; node = node.parentElement) {
    if (node.scrollHeight <= node.clientHeight)
      continue
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll')
      return node
  }
  return null
}

// 容器內讀者實際看得到的範圍。彈出版型的 action bar 是疊在捲動區底部的 sticky 條，座標上在容器
// 內、實際被它蓋住，所以下緣取容器下緣與 action bar 上緣的較小值。沒有高度的 action bar（隱藏時
// 量到全 0 的矩形）不算：它什麼都沒蓋住，拿它的 0 當界線會在容器上緣捲出視窗時把範圍壓成 0。
function visibleRange(container: HTMLElement): VerticalRange {
  const box = container.getBoundingClientRect()
  const nav = container.querySelector('.nav')?.getBoundingClientRect()
  const bottom = nav !== undefined && nav.height > 0 && nav.top > box.top ? Math.min(box.bottom, nav.top) : box.bottom
  return { top: box.top, bottom }
}

/**
 * 在 root 底下找第一個標了 `data-invalid` 的題目（active.ts 的 stepView 掛的錨點），讓它的標題與
 * 錯誤說明同時可見。沒有出錯的題目就什麼都不做。jsdom 不排版、也沒實作 scrollIntoView，
 * 容器會是 null、scrollIntoView 以 optional call 略過。
 */
export function revealFirstInvalid(root: ParentNode): void {
  const step = root.querySelector<HTMLElement>('[data-invalid]')
  if (step === null)
    return
  const title = step.querySelector<HTMLElement>('.q-title')
  const error = step.querySelector<HTMLElement>('.q-error')
  if (title === null || error === null) {
    step.scrollIntoView?.({ block: 'nearest' })
    return
  }
  const container = scrollContainerOf(title)
  if (container !== null) {
    const next = revealScrollTop(
      container.scrollTop,
      visibleRange(container),
      { top: title.getBoundingClientRect().top, bottom: error.getBoundingClientRect().bottom },
    )
    if (next !== container.scrollTop)
      container.scrollTop = next
  }
  title.scrollIntoView?.({ block: 'nearest' })
  error.scrollIntoView?.({ block: 'nearest' })
}
